import React, { useCallback, useEffect, useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from '@/common/hooks/index';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_MAX_PAGE_SIZE = 100;
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const NO_SELECTION_STYLES = {
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'transparent',
  },
  '& .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: 'transparent',
  },
};

const toPositiveNumber = (value, fallback) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }

  return parsed;
};

const clampPageSize = (value, maxPageSize) =>
  Math.min(toPositiveNumber(value, DEFAULT_PAGE_SIZE), maxPageSize);

const normalizeParams = (params) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (value === '' || value === null || value === undefined) {
      return acc;
    }

    acc[key] = String(value);

    return acc;
  }, {});

const areParamsEqual = (left, right) => {
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);

  if (leftEntries.length !== rightEntries.length) {
    return false;
  }

  return leftEntries.every(([key, value]) => right[key] === value);
};

const DataGridTable = ({
  queryKey,
  queryFn,
  columns = [],
  mapRows = (items) => items,
  getRows = (response) => response?.data ?? [],
  getPagination = (response) => response?.pagination ?? {},
  queryOptions = {},
  defaultQueryParams = {},
  queryParamNames = {},
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  maxPageSize = DEFAULT_MAX_PAGE_SIZE,
  syncPaginationWithQuery = true,
  syncSortWithQuery = false,
  renderFilters,
  emptyState,
  emptyStateClassName,
  onRowClick,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
  loading = false,
  ...dataGridProps
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const pageParam = queryParamNames.page || 'page';
  const limitParam = queryParamNames.limit || 'limit';
  const sortByParam = queryParamNames.sortBy || 'sortBy';
  const sortOrderParam = queryParamNames.sortOrder || 'sortOrder';
  const defaultPageSize =
    rowsPerPageOptions.find((option) => option === DEFAULT_PAGE_SIZE) ||
    rowsPerPageOptions[0] ||
    DEFAULT_PAGE_SIZE;

  const queryDefaults = useMemo(
    () => ({
      ...(syncPaginationWithQuery
        ? {
            [pageParam]: '1',
            [limitParam]: String(defaultPageSize),
          }
        : {}),
      ...(syncSortWithQuery
        ? {
            [sortByParam]: '',
            [sortOrderParam]: '',
          }
        : {}),
      ...defaultQueryParams,
    }),
    [
      defaultPageSize,
      defaultQueryParams,
      limitParam,
      pageParam,
      sortByParam,
      sortOrderParam,
      syncPaginationWithQuery,
      syncSortWithQuery,
    ]
  );

  const { params, setQueryParams } = useQueryParams(queryDefaults);

  const updateTableParams = useCallback(
    (updates, options = {}) => {
      const { resetPage = false } = options;
      const nextUpdates = { ...updates };
      const hasActualChanges = Object.entries(updates).some(
        ([key, value]) => String(params[key] ?? '') !== String(value ?? '')
      );

      if (syncPaginationWithQuery && resetPage && hasActualChanges) {
        nextUpdates[pageParam] = '1';
      }

      setQueryParams(nextUpdates);
    },
    [pageParam, params, setQueryParams, syncPaginationWithQuery]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const missingParams = Object.entries(queryDefaults).reduce(
      (acc, [key, value]) => {
        if (!searchParams.has(key)) {
          acc[key] = value;
        }

        return acc;
      },
      {}
    );

    if (!Object.keys(missingParams).length) return;

    setQueryParams(missingParams);
  }, [location.search, queryDefaults, setQueryParams]);

  const page = syncPaginationWithQuery
    ? toPositiveNumber(params[pageParam], 1)
    : 1;
  const pageSize = syncPaginationWithQuery
    ? clampPageSize(params[limitParam], maxPageSize)
    : defaultPageSize;
  const sortBy = syncSortWithQuery ? params[sortByParam] : '';
  const sortOrder = syncSortWithQuery
    ? (params[sortOrderParam] || 'ASC').toUpperCase()
    : '';

  const requestParams = useMemo(() => {
    const nextParams = { ...params };

    if (syncPaginationWithQuery) {
      nextParams[pageParam] = page;
      nextParams[limitParam] = pageSize;
    }

    if (syncSortWithQuery) {
      if (sortBy) {
        nextParams[sortByParam] = sortBy;
        nextParams[sortOrderParam] = sortOrder;
      } else {
        delete nextParams[sortByParam];
        delete nextParams[sortOrderParam];
      }
    }

    return nextParams;
  }, [
    limitParam,
    page,
    pageParam,
    pageSize,
    params,
    sortBy,
    sortByParam,
    sortOrder,
    sortOrderParam,
    syncPaginationWithQuery,
    syncSortWithQuery,
  ]);

  const resolvedQueryKey = useMemo(() => {
    if (Array.isArray(queryKey)) {
      return [...queryKey, requestParams];
    }

    return [queryKey, requestParams];
  }, [queryKey, requestParams]);

  const { data, isLoading, isFetching } = useQuery({
    placeholderData: keepPreviousData,
    queryKey: resolvedQueryKey,
    queryFn: () => queryFn(requestParams),
    ...queryOptions,
  });

  const rows = useMemo(
    () => mapRows(getRows(data), data),
    [data, getRows, mapRows]
  );
  const pagination = useMemo(() => getPagination(data), [data, getPagination]);
  const normalizedDefaultParams = useMemo(() => {
    const nextParams = { ...queryDefaults };

    if (syncPaginationWithQuery) {
      nextParams[pageParam] = '1';
      nextParams[limitParam] = String(defaultPageSize);
    }

    if (syncSortWithQuery) {
      if (nextParams[sortByParam]) {
        nextParams[sortOrderParam] = (
          nextParams[sortOrderParam] || 'ASC'
        ).toUpperCase();
      } else {
        delete nextParams[sortByParam];
        delete nextParams[sortOrderParam];
      }
    }

    return normalizeParams(nextParams);
  }, [
    defaultPageSize,
    limitParam,
    pageParam,
    queryDefaults,
    sortByParam,
    sortOrderParam,
    syncPaginationWithQuery,
    syncSortWithQuery,
  ]);
  const showEmptyState = Boolean(
    emptyState &&
      !loading &&
      !isLoading &&
      !isFetching &&
      rows.length === 0 &&
      areParamsEqual(normalizeParams(requestParams), normalizedDefaultParams)
  );
  const sortModel = useMemo(() => {
    if (!syncSortWithQuery || !sortBy) {
      return [];
    }

    return [{ field: sortBy, sort: sortOrder.toLowerCase() }];
  }, [sortBy, sortOrder, syncSortWithQuery]);

  const clearMouseFocus = useCallback((event) => {
    if (event.detail === 0) {
      return;
    }

    requestAnimationFrame(() => {
      const activeElement = document.activeElement;

      if (
        activeElement instanceof HTMLElement &&
        (activeElement.getAttribute('role') === 'gridcell' ||
          activeElement.getAttribute('role') === 'columnheader' ||
          activeElement.closest('[role="gridcell"]') ||
          activeElement.closest('[role="columnheader"]'))
      ) {
        activeElement.blur();
      }
    });
  }, []);

  if (showEmptyState) {
    return <div className={emptyStateClassName}>{emptyState}</div>;
  }

  return (
    <>
      {renderFilters?.({
        params: requestParams,
        setQueryParams,
        updateTableParams,
      })}
      <DataGrid
        columns={columns}
        rows={rows}
        {...dataGridProps}
        disableColumnMenu
        disableRowSelectionOnClick
        rowSelection={false}
        loading={loading || isLoading || isFetching}
        pagination={syncPaginationWithQuery}
        paginationMode={syncPaginationWithQuery ? 'server' : 'client'}
        paginationModel={{
          page: Math.max(page - 1, 0),
          pageSize,
        }}
        rowCount={pagination?.total ?? rows.length}
        pageSizeOptions={rowsPerPageOptions}
        sortingMode={syncSortWithQuery ? 'server' : 'client'}
        sortingOrder={
          dataGridProps.sortingOrder ||
          (syncSortWithQuery ? ['asc', 'desc'] : undefined)
        }
        sortModel={sortModel}
        localeText={{
          noRowsLabel: t('No results found'),
          columnHeaderSortIconLabel: '',
          ...dataGridProps.localeText,
        }}
        sx={{
          ...NO_SELECTION_STYLES,
          ...dataGridProps.sx,
        }}
        onPaginationModelChange={(nextPaginationModel, details) => {
          const { page: nextPage = 0, pageSize: nextPageSize = pageSize } =
            nextPaginationModel;
          const didPageSizeChange = nextPageSize !== pageSize;

          if (syncPaginationWithQuery) {
            updateTableParams(
              {
                [pageParam]: String(nextPage + 1),
                [limitParam]: String(clampPageSize(nextPageSize, maxPageSize)),
              },
              { resetPage: didPageSizeChange }
            );
          }

          if (nextPage !== Math.max(page - 1, 0)) {
            onPageChange?.(nextPage, details);
          }

          if (didPageSizeChange) {
            onPageSizeChange?.(nextPageSize, details);
          }
        }}
        onSortModelChange={(nextSortModel, details) => {
          if (syncSortWithQuery) {
            const [nextSort] = nextSortModel;

            updateTableParams(
              {
                [sortByParam]: nextSort?.field || '',
                [sortOrderParam]: nextSort?.sort
                  ? nextSort.sort.toUpperCase()
                  : '',
              },
              { resetPage: true }
            );
          }

          onSortModelChange?.(nextSortModel, details);
        }}
        onCellClick={(params, event, details) => {
          clearMouseFocus(event);
          dataGridProps.onCellClick?.(params, event, details);
        }}
        onColumnHeaderClick={(params, event) => {
          clearMouseFocus(event);
          dataGridProps.onColumnHeaderClick?.(params, event);
        }}
        onRowClick={(params, event, details) => {
          clearMouseFocus(event);
          onRowClick?.(params, event, details);
        }}
      />
    </>
  );
};

export default DataGridTable;
